import React, { useEffect, useState } from 'react';
import { IndianRupee, Clock, CheckCircle, XCircle, TrendingUp, Download, Calendar, CreditCard, Settings, Banknote } from 'lucide-react';
import './Payouts.css';
import { API_BASE_URL } from '../../api';

const Payouts = () => {
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [verifyingBank, setVerifyingBank] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [bankDetails, setBankDetails] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [sRes, pRes, setRes, prevRes, earningsRes, bankRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/payouts/summary`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/payouts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/payouts/settings`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/payouts/preview`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/vendor/earnings?period=${selectedPeriod}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/vendor/bank-details`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (sRes.ok) setSummary(await sRes.json());
      if (pRes.ok) setPayouts(await pRes.json());
      if (setRes.ok) setSettings(await setRes.json());
      if (prevRes.ok) setPreview(await prevRes.json());
      if (earningsRes.ok) setEarnings(await earningsRes.json());
      if (bankRes.ok) {
        const bankData = await bankRes.json();
        setBankDetails(bankData.bankDetails);
      }
    } catch (e) {
      console.error('Error loading payouts:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedPeriod]);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);

  const StatusIcon = ({ status }) => {
    if (status === 'paid' || status === 'verified') return <CheckCircle size={16} className="status-paid" />;
    if (status === 'pending') return <Clock size={16} className="status-pending" />;
    return <XCircle size={16} className="status-failed" />;
  };

  const getStatusIcon = (status) => {
    if (status === 'verified') return <CheckCircle size={16} />;
    if (status === 'pending') return <Clock size={16} />;
    return <XCircle size={16} />;
  };

  const saveSettings = async () => {
    try {
      setSavingSettings(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/payouts/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: settings.mode,
          day_of_week: Number(settings.day_of_week ?? 5),
          min_payout_amount: Number(settings.min_payout_amount ?? 0),
          bank_account_holder: settings.bank_account_holder || '',
          bank_account_number: settings.bank_account_number || '',
          bank_ifsc: settings.bank_ifsc || ''
        })
      });
      if (!res.ok) {
        const e = await res.json();
        alert(e.error || 'Failed to save settings');
      } else {
        await fetchData();
      }
    } catch (e) {
      console.error('Save settings error:', e);
    } finally {
      setSavingSettings(false);
    }
  };

  const verifyBank = async () => {
    try {
      setVerifyingBank(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/payouts/bank-verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bank_account_holder: settings.bank_account_holder,
          bank_account_number: settings.bank_account_number,
          bank_ifsc: settings.bank_ifsc
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Verification failed');
      } else {
        setSettings(prev => ({ ...prev, is_bank_verified: 1 }));
      }
    } catch (e) {
      console.error('Verify bank error:', e);
    } finally {
      setVerifyingBank(false);
    }
  };

  const requestManualPayout = async () => {
    try {
      setRequestingPayout(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/payouts/request`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: Number(manualAmount || 0), method: 'bank_transfer' })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Payout request failed');
      } else {
        setManualAmount('');
        await fetchData();
      }
    } catch (e) {
      console.error('Manual payout request error:', e);
    } finally {
      setRequestingPayout(false);
    }
  };

  const downloadStatement = async (payoutId, format = 'pdf') => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/payouts/${payoutId}/statement?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payout-statement-${payoutId}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download statement');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Error downloading statement');
    }
  };

  const getPeriodLabel = (period) => {
    switch (period) {
      case 'current_month': return 'Current Month';
      case 'last_month': return 'Last Month';
      case 'last_30_days': return 'Last 30 Days';
      case 'last_7_days': return 'Last 7 Days';
      default: return 'Current Month';
    }
  };

  return (
    <div className="payouts-container">
      <div className="payouts-header">
        <div>
          <h1 className="payouts-title">
            <IndianRupee size={28} /> Payouts
          </h1>
          <p className="payouts-subtitle">Manage your earnings and payout settings</p>
        </div>
      </div>
      {loading ? (
        <div className="loading">Loading payouts...</div>
      ) : (
        <>
          {summary && (
            <div className="payouts-summary">
              <div className="summary-card earnings">
                <div className="summary-card-header">
                  <div className="summary-card-icon">
                    <TrendingUp size={24} />
                  </div>
                </div>
                <div className="summary-card-label">Total Earnings</div>
                <div className="summary-card-value">{formatCurrency(summary.totalEarnings)}</div>
              </div>
              <div className="summary-card pending">
                <div className="summary-card-header">
                  <div className="summary-card-icon">
                    <Clock size={24} />
                  </div>
                </div>
                <div className="summary-card-label">Pending Payout</div>
                <div className="summary-card-value">{formatCurrency(summary.pendingPayout)}</div>
              </div>
              <div className="summary-card last">
                <div className="summary-card-header">
                  <div className="summary-card-icon">
                    <CheckCircle size={24} />
                  </div>
                </div>
                <div className="summary-card-label">Last Payout</div>
                <div className="summary-card-value">{formatCurrency(summary.lastPayout)}</div>
              </div>
              <div className="summary-card next">
                <div className="summary-card-header">
                  <div className="summary-card-icon">
                    <Calendar size={24} />
                  </div>
                </div>
                <div className="summary-card-label">Next Payout</div>
                <div className="summary-card-value">{summary.nextPayoutDate}</div>
              </div>
              {preview && (
                <div className="summary-card estimated">
                  <div className="summary-card-header">
                    <div className="summary-card-icon">
                      <Banknote size={24} />
                    </div>
                  </div>
                  <div className="summary-card-label">Estimated Next Payout</div>
                  <div className="summary-card-value">{formatCurrency(preview.estimatedAmount)}</div>
                  <div className="summary-card-subtext">
                    {preview.mode === 'auto' && preview.nextPayoutDate ? `Scheduled: ${preview.nextPayoutDate}` : 'Manual mode'}
                    {(!preview.meetsMinimum) ? ` (min ${formatCurrency(preview.minimumRequired)})` : ''}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Earnings Breakdown Section */}
          {earnings && (
            <div className="earnings-section">
              <div className="earnings-header">
                <h2 className="earnings-title">
                  <TrendingUp size={20} />
                  Earnings Breakdown - {getPeriodLabel(selectedPeriod)}
                </h2>
                <div className="period-selector">
                  <select 
                    value={selectedPeriod} 
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="period-select"
                  >
                    <option value="current_month">Current Month</option>
                    <option value="last_month">Last Month</option>
                    <option value="last_30_days">Last 30 Days</option>
                    <option value="last_7_days">Last 7 Days</option>
                  </select>
                </div>
              </div>

              <div className="earnings-breakdown">
                <div className="earnings-card">
                  <div className="earnings-label">Total Sales (Gross Revenue)</div>
                  <div className="earnings-value positive">{formatCurrency(earnings.earnings.total_sales)}</div>
                </div>
                <div className="earnings-card">
                  <div className="earnings-label">Platform Commission</div>
                  <div className="earnings-value negative">-{formatCurrency(earnings.earnings.platform_commission)}</div>
                </div>
                <div className="earnings-card">
                  <div className="earnings-label">Delivery Fee Share</div>
                  <div className="earnings-value negative">-{formatCurrency(earnings.earnings.delivery_fee_share)}</div>
                </div>
                <div className="earnings-card">
                  <div className="earnings-label">TDS (Tax Deducted at Source)</div>
                  <div className="earnings-value negative">-{formatCurrency(earnings.earnings.tds_amount)}</div>
                </div>
                <div className="earnings-card">
                  <div className="earnings-label">GST</div>
                  <div className="earnings-value negative">-{formatCurrency(earnings.earnings.gst_amount)}</div>
                </div>
                <div className="earnings-card total">
                  <div className="earnings-label">Net Earnings (Payable)</div>
                  <div className="earnings-value total">{formatCurrency(earnings.earnings.net_earnings)}</div>
                </div>
              </div>

              {earnings.schedule && (
                <div className="payout-schedule">
                  <h4><Calendar size={16} /> Payout Schedule</h4>
                  <div className="schedule-details">
                    <div className="schedule-item">
                      <span className="schedule-label">Frequency:</span>
                      <span className="schedule-value">{earnings.schedule.frequency}</span>
                    </div>
                    <div className="schedule-item">
                      <span className="schedule-label">Minimum Amount:</span>
                      <span className="schedule-value">{formatCurrency(earnings.schedule.min_payout_amount)}</span>
                    </div>
                    {earnings.schedule.next_payout_date && (
                      <div className="schedule-item">
                        <span className="schedule-label">Next Payout:</span>
                        <span className="schedule-value">{new Date(earnings.schedule.next_payout_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bank Details Status */}
          {bankDetails && (
            <div className="bank-section">
              <div className="bank-header">
                <h2 className="bank-title">
                  <CreditCard size={20} />
                  Bank Details Status
                </h2>
              </div>
              <div className="bank-status">
                <div className="bank-info">
                  <div className="bank-detail">
                    <span className="bank-label">Account Holder</span>
                    <span className="bank-value">{bankDetails.account_holder_name}</span>
                  </div>
                  <div className="bank-detail">
                    <span className="bank-label">Bank</span>
                    <span className="bank-value">{bankDetails.bank_name}</span>
                  </div>
                  <div className="bank-detail">
                    <span className="bank-label">IFSC Code</span>
                    <span className="bank-value">{bankDetails.ifsc_code}</span>
                  </div>
                  {bankDetails.upi_id && (
                    <div className="bank-detail">
                      <span className="bank-label">UPI ID</span>
                      <span className="bank-value">{bankDetails.upi_id}</span>
                    </div>
                  )}
                </div>
                <div className="bank-verification">
                  <span className={`verification-status ${bankDetails.verification_status.toLowerCase()}`}>
                    {bankDetails.verification_status}
                    <StatusIcon status={bankDetails.verification_status} />
                  </span>
                </div>
              </div>
            </div>
          )}

          {settings && (
            <div className="settings-section">
              <div className="settings-header">
                <h2 className="settings-title">
                  <Settings size={20} />
                  Payout Settings
                </h2>
              </div>
              <div className="settings-grid">
                <div className="settings-row">
                  <label>Payout Mode</label>
                  <select
                    value={settings.mode || 'auto'}
                    onChange={(e) => setSettings(prev => ({ ...prev, mode: e.target.value }))}
                  >
                    <option value="auto">Automatic (weekly)</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div className="settings-row">
                  <label>Weekly Payout Day</label>
                  <select
                    value={settings.day_of_week ?? 5}
                    onChange={(e) => setSettings(prev => ({ ...prev, day_of_week: Number(e.target.value) }))}
                    disabled={settings.mode !== 'auto'}
                  >
                    <option value={0}>Sunday</option>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                  </select>
                </div>
                <div className="settings-row">
                  <label>Minimum Payout Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={settings.min_payout_amount ?? 0}
                    onChange={(e) => setSettings(prev => ({ ...prev, min_payout_amount: e.target.value }))}
                  />
                </div>
              </div>

              <div className="settings-header" style={{ marginTop: 32 }}>
                <h2 className="settings-title">
                  <CreditCard size={20} />
                  Bank Details
                </h2>
              </div>
              <div className="settings-grid">
                <div className="settings-row">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    value={settings.bank_account_holder || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_account_holder: e.target.value }))}
                  />
                </div>
                <div className="settings-row">
                  <label>Account Number</label>
                  <input
                    type="text"
                    value={settings.bank_account_number || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_account_number: e.target.value }))}
                  />
                </div>
                <div className="settings-row">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    value={settings.bank_ifsc || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, bank_ifsc: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div className="settings-row">
                  <label>Verification Status</label>
                  <div>
                    {settings.is_bank_verified ? (
                      <span className="status-badge paid">Verified</span>
                    ) : (
                      <button 
                        className="btn btn-secondary" 
                        onClick={verifyBank} 
                        disabled={verifyingBank}
                      >
                        {verifyingBank ? 'Verifying...' : 'Verify Bank'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="settings-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={saveSettings} 
                  disabled={savingSettings}
                >
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}

          {/* Manual Payout Section */}
          {settings && (
            <div className="manual-payout-section">
              <div className="manual-payout-header">
                <h2 className="manual-payout-title">
                  <Banknote size={20} />
                  Manual Payout Request
                </h2>
              </div>
              <div className="manual-payout-form">
                <div className="manual-payout-input">
                  <label>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                    placeholder="Enter amount to withdraw"
                  />
                </div>
                <button
                  className="btn btn-success"
                  onClick={requestManualPayout}
                  disabled={requestingPayout || !settings.is_bank_verified || Number(manualAmount || 0) <= 0}
                >
                  {requestingPayout ? 'Processing...' : 'Request Payout'}
                </button>
              </div>
            </div>
          )}
          <div className="payouts-history">
            <div className="payouts-history-header">
              <h2 className="payouts-history-title">
                <Download size={20} />
                Payout History
              </h2>
            </div>
            <table className="payouts-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th>Created</th>
                  <th>Paid At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id}>
                    <td className="font-mono font-semibold">#{p.id}</td>
                    <td className="amount-cell">{formatCurrency(p.amount)}</td>
                    <td className="status-cell">
                      <span className={`status-badge ${p.status}`}>
                        {p.status} <StatusIcon status={p.status} />
                      </span>
                    </td>
                    <td className="method-cell">{p.method?.replace('_',' ')}</td>
                    <td className="font-mono text-sm">{p.reference || '-'}</td>
                    <td className="date-cell">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="date-cell">{p.paid_at ? new Date(p.paid_at).toLocaleString() : '-'}</td>
                    <td className="actions-cell">
                      {p.status === 'paid' && (
                        <div className="download-actions">
                          <button 
                            onClick={() => downloadStatement(p.id, 'pdf')}
                            className="download-btn"
                            title="Download PDF Statement"
                          >
                            <Download size={14} />
                            PDF
                          </button>
                          <button 
                            onClick={() => downloadStatement(p.id, 'excel')}
                            className="download-btn"
                            title="Download Excel Statement"
                          >
                            <Download size={14} />
                            Excel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {payouts.length === 0 && (
                  <tr><td colSpan="8" className="empty-cell">No payouts yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Payouts;


